// ─────────────────────────────────────────────────────────────────────────────
// LOCAL UI-PREVIEW MODE  (dev-only, no backend)  — for iterating on the UI/CSS.
// Active ONLY when Vite runs with `--mode uipreview` (npm run dev:ui). Off in every
// normal build/dev run, where `req()` never calls in here and this file is inert.
// ─────────────────────────────────────────────────────────────────────────────

export const PREVIEW: boolean = (import.meta as any).env?.VITE_UI_PREVIEW === '1';

const NOW = Date.now();
const min = (n: number) => new Date(NOW - n * 60_000).toISOString();
const WS = 'ws_fxm';

const ME = {
  id: 'u_me', email: 'you@futureobvious.com', name: 'You', avatar_url: null,
  presence: 'online', status_mode: 'active', timezone: 'America/New_York',
  prefs: { appearance: { theme_name: 'ochre', color_mode: 'dark', compact: false } },
};

const MEMBERS = [
  { id: 'u_me', name: 'You', email: 'you@futureobvious.com', presence: 'online', role: 'owner' },
  { id: 'u_arjun', name: 'Arjun Mehta', email: 'arjun@futureobvious.com', presence: 'online', role: 'admin' },
  { id: 'u_parishi', name: 'Parishi Shah', email: 'parishi@futureobvious.com', presence: 'online', role: 'member' },
  { id: 'u_dana', name: 'Dana Wolfe', email: 'dana@futureobvious.com', presence: 'away', role: 'member' },
  { id: 'u_marco', name: 'Marco Ruiz', email: 'marco@futureobvious.com', presence: 'offline', role: 'member' },
];

const AGENTS = [
  { id: 'a_duke', name: 'Duke', avatar_url: null },
  { id: 'a_kylie', name: 'Kylie', avatar_url: null },
];

const CHANNELS = [
  { id: 'ch_dispatch', name: 'dispatch', kind: 'public', topic: 'Field dispatch coordination — Duke posts rollout briefs here', last_message_at: min(1), unread: false, mentions: 0, mode: 'chat' },
  { id: 'ch_general', name: 'general', kind: 'public', topic: 'Company-wide announcements and watercooler', last_message_at: min(40), unread: true, mentions: 2, mode: 'chat' },
  { id: 'ch_engineering', name: 'engineering', kind: 'public', topic: 'Builds, deploys, incidents', last_message_at: min(120), unread: true, mentions: 0, mode: 'chat' },
  { id: 'ch_design', name: 'design', kind: 'public', topic: 'Product design & UI polish', last_message_at: min(300), unread: false, mentions: 0, mode: 'chat' },
  { id: 'ch_monitoring', name: 'monitoring', kind: 'private', topic: 'Kylie watches the fleet here', last_message_at: min(15), unread: false, mentions: 0, mode: 'chat' },
];

const DMS = [
  { id: 'dm_arjun', name: 'Arjun Mehta', kind: 'dm', last_message_at: min(8), unread: false, mentions: 0,
    dm_with: { type: 'user', id: 'u_arjun', name: 'Arjun Mehta', presence: 'online' } },
  { id: 'dm_duke', name: 'Duke', kind: 'dm', last_message_at: min(22), unread: true, mentions: 1,
    dm_with: { type: 'agent', id: 'a_duke', name: 'Duke', presence: 'online' } },
];

const react = (emoji: string, count: number, actors: string[]) => ({ emoji, count, actors });

const DISPATCH_MESSAGES = [
  { id: 'm1', channel_id: 'ch_dispatch', sender_type: 'user', sender_id: 'u_arjun', sender_name: 'Arjun Mehta',
    body: 'Morning all — 6 jobs came in overnight for the north region. Duke, can you build the rollout brief?',
    created_at: min(48), reactions: [react('👍', 2, ['u_me', 'u_parishi'])] },
  { id: 'm2', channel_id: 'ch_dispatch', sender_type: 'agent', sender_id: 'a_duke', sender_name: 'Duke',
    body: 'On it. Pulling the 6 work orders from BuildOps and cross-checking tech availability against Samsara geofences.',
    created_at: min(47) },
  { id: 'm3', channel_id: 'ch_dispatch', sender_type: 'agent', sender_id: 'a_duke', sender_name: 'Duke',
    body: '**Rollout brief — North Region (6 jobs)**\n\n'
      + '1. **#14312** · HVAC no-cool · *Acme Retail #204* · NTE **$1,500** → Jarrett\n'
      + '2. **#14318** · Refrigeration · *ColdChain Foods* · NTE **$3,200** → Jacob\n'
      + '3. **#14320** · Electrical · *Northgate Mall* · NTE **$900** → Marco\n\n'
      + '…3 more in thread. All techs within 20 min of site. Confirm and I\'ll dispatch.',
    created_at: min(46), reactions: [react('🔥', 1, ['u_arjun']), react('🚀', 2, ['u_me', 'u_parishi'])], reply_count: 3, last_reply_at: min(44) },
  { id: 'm4', channel_id: 'ch_dispatch', sender_type: 'user', sender_id: 'u_parishi', sender_name: 'Parishi Shah',
    body: 'Looks right. #14318 is over $3k though — that needs sign-off before we send a tech.', created_at: min(40) },
  { id: 'm5', channel_id: 'ch_dispatch', sender_type: 'agent', sender_id: 'a_duke', sender_name: 'Duke',
    body: '⚠️ **Approval needed** — Job **#14318** (ColdChain Foods) exceeds the $3,000 auto-dispatch limit.\n\n'
      + 'NTE **$3,200** · assigned **Jacob** · ETA 18 min.\n\n'
      + 'Reply **approve** to dispatch, or **hold** to keep it queued. I\'ll wait for a human here.', created_at: min(38) },
  { id: 'm6', channel_id: 'ch_dispatch', sender_type: 'user', sender_id: 'u_me', sender_name: 'You',
    body: 'approve ✅ — go ahead and dispatch all six.', created_at: min(2), reactions: [react('✅', 1, ['u_arjun'])] },
  { id: 'm7', channel_id: 'ch_dispatch', sender_type: 'agent', sender_id: 'a_duke', sender_name: 'Duke',
    body: 'Dispatched all 6 jobs. Techs notified via SMS, ETAs posted to each work order. I\'ll flag any that slip past ETA.',
    created_at: min(1), reactions: [react('🙌', 3, ['u_me', 'u_arjun', 'u_parishi'])] },
];

const GENERAL_MESSAGES = [
  { id: 'g1', channel_id: 'ch_general', sender_type: 'user', sender_id: 'u_dana', sender_name: 'Dana Wolfe',
    body: 'Reminder: all-hands moved to 2pm ET today. @You @Arjun Mehta please add your updates to the deck.', created_at: min(40) },
];

const MESSAGES_BY_CHANNEL: Record<string, any[]> = { ch_dispatch: DISPATCH_MESSAGES, ch_general: GENERAL_MESSAGES };

let sendSeq = 0;
function echoMessage(cid: string, body: string): any {
  sendSeq += 1;
  return { id: `m_sent_${sendSeq}`, channel_id: cid, sender_type: 'user', sender_id: 'u_me',
    sender_name: 'You', body, created_at: new Date().toISOString(), reactions: [] };
}

export function mockResponse(path: string, opts: RequestInit = {}): any {
  const method = (opts.method || 'GET').toUpperCase();
  const p = path.split('?')[0];

  if (p === '/me') return ME;
  if (p === '/auth/providers') return { google: true, microsoft: true };
  if (p === '/realtime/token' || p.startsWith('/realtime/') || p.endsWith('/subscribe-token')) return { token: 'preview' };
  if (p === '/me/org') return { linked: false, org: null };
  if (p === '/me/join-requests') return [];

  if (p === '/workspaces') return [
    { id: WS, name: 'Force Multiplier', slug: 'fxm', role: 'owner' },
    { id: 'ws_acme', name: 'Acme Field Services', slug: 'acme', role: 'member' },
  ];
  if (p === `/workspaces/${WS}/channels`) return CHANNELS;
  if (p === `/workspaces/${WS}/dms`) return DMS;
  if (p === `/workspaces/${WS}/agents`) return AGENTS;
  if (p === `/workspaces/${WS}/members`) return MEMBERS;

  const mMsgs = p.match(/^\/channels\/([^/]+)\/messages$/);
  if (mMsgs) {
    if (method === 'POST') return echoMessage(mMsgs[1], safeBody(opts).body || '');
    return MESSAGES_BY_CHANNEL[mMsgs[1]] || [];
  }
  if (/^\/channels\/[^/]+\/members$/.test(p)) return { users: MEMBERS, agents: AGENTS, count: MEMBERS.length + AGENTS.length };
  if (/^\/channels\/[^/]+\/reads$/.test(p)) return [];
  if (/^\/channels\/[^/]+\/pins$/.test(p)) return [];
  if (/^\/channels\/[^/]+\/canvas$/.test(p)) return { body: '' };
  if (/^\/channels\/[^/]+\/read$/.test(p)) return { ok: true, last_read_at: new Date().toISOString() };
  if (/^\/channels\/[^/]+\/typing$/.test(p)) return { ok: true };

  // Object-shaped endpoints some views map over — return the empty container shape so
  // views like KnowledgeView/Jira/Calendar don't crash on `.entries.map` etc.
  if (/\/kb(\?|$)/.test(path)) return { entries: [] };
  if (p.includes('/calendar')) return { events: [], connected: [] };
  if (p.endsWith('/checklist') || p.endsWith('/pins')) return [];
  if (p.includes('/jira')) return { connected: false };
  if (p.endsWith('/canvas')) return { body: '' };
  if (p.includes('/incident')) return null;
  if (p.includes('/poll')) return null;

  if (method !== 'GET') return { ok: true };
  return [];
}

function safeBody(opts: RequestInit): any {
  try { return typeof opts.body === 'string' ? JSON.parse(opts.body) : {}; }
  catch { return {}; }
}
